import { Anchor, Badge, Box, Button, Group, Modal, Paper, Select, Stack, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString, resolveId } from '@medplum/core';
import type { GuidanceResponse, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  PATIENT_EVALUATION_STATUS,
  PATIENT_TRIAGE_STATUS,
  POLARIS_EVALUATION_TAG_SYSTEM,
  replaceTriageStatusTags,
} from '@polarissleep/shared';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useRole } from '../../hooks/useRole';
import { botIdentifier } from '../../lib/bot-identifier';
import { hasPermission } from '../../utils/permissions';
import { withTenantScope } from '../../utils/tenant';

const PERFORMER_EXTENSION_URL = 'http://polarissleep.com/extensions/performer';
const DENIAL_REASON_SYSTEM = 'http://polarissleep.com/guidance/denial-reason';
const HSAT_ELIGIBILITY_MODULE_URI = 'http://polarissleep.com/guidance/hsat-eligibility';

const DENIAL_REASONS = [
  { value: 'not-medically-necessary', label: 'Not Medically Necessary' },
  {
    value: 'not-credentialed',
    label: "Not Credentialed with Patient's Insurer",
  },
  {
    value: 'recent-sleep-test',
    label: 'Recent Sleep Test, Ineligible for Retest',
  },
  { value: 'hsat-insufficient', label: 'HSAT Insufficient, In-Lab Required' },
];

export interface IntakeEvaluationModalProps {
  opened: boolean;
  onClose: () => void;
  patientId: string | undefined;
  patientName: string;
  isAlreadyApproved?: boolean;
}

export function IntakeEvaluationModal({
  opened,
  onClose,
  patientId,
  patientName,
  isAlreadyApproved = false,
}: IntakeEvaluationModalProps): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role, isAdmin } = useRole();
  const canEvaluate = hasPermission({
    role,
    permission: 'evaluate-triage',
    isAdmin,
  });
  const isCareCoordinator = role === 'care-coordinator';
  const [approved, setApproved] = useState(false);
  const [denied, setDenied] = useState(false);
  const [denialReason, setDenialReason] = useState('');
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const [submittingDenial, setSubmittingDenial] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [alreadyApproved, setAlreadyApproved] = useState(isAlreadyApproved);

  useEffect(() => {
    if (!opened || !patientId) {
      setAlreadyApproved(isAlreadyApproved);
      return;
    }
    setLoadingStatus(true);
    Promise.all([
      medplum.readResource('Patient', patientId) as Promise<Patient>,
      medplum.searchResources('GuidanceResponse', { subject: `Patient/${patientId}` }),
    ])
      .then(([patient, responses]) => {
        const patientTagApproved =
          patient.meta?.tag?.some(
            (t) => t.system === POLARIS_EVALUATION_TAG_SYSTEM && t.code === PATIENT_EVALUATION_STATUS.approved
          ) ?? false;
        const guidanceApproved = responses.some(
          (r) => r.moduleUri === HSAT_ELIGIBILITY_MODULE_URI && r.status === 'success'
        );
        setAlreadyApproved(patientTagApproved || guidanceApproved);
      })
      .catch((err) => {
        showNotification({ color: 'red', message: normalizeErrorString(err) });
      })
      .finally(() => {
        setLoadingStatus(false);
      });
  }, [opened, patientId, medplum, isAlreadyApproved]);

  function handleClose(): void {
    setApproved(false);
    setDenied(false);
    setDenialReason('');
    setAlreadyApproved(isAlreadyApproved);
    onClose();
  }

  function buildBaseResource(): Omit<GuidanceResponse, 'status'> {
    return {
      resourceType: 'GuidanceResponse',
      moduleUri: HSAT_ELIGIBILITY_MODULE_URI,
      subject: patientId ? { reference: `Patient/${patientId}` } : undefined,
      occurrenceDateTime: new Date().toISOString(),
      extension: profile
        ? [
            {
              url: PERFORMER_EXTENSION_URL,
              valueReference: createReference(profile),
            },
          ]
        : undefined,
    };
  }

  async function handleApproveSubmit(): Promise<void> {
    if (!canEvaluate || !patientId) {
      return;
    }
    setSubmittingApproval(true);
    try {
      const [patient, existingResponses] = await Promise.all([
        medplum.readResource('Patient', patientId) as Promise<Patient>,
        medplum.searchResources('GuidanceResponse', { subject: `Patient/${patientId}` }),
      ]);

      const patientTagApproved = patient.meta?.tag?.some(
        (t) => t.system === POLARIS_EVALUATION_TAG_SYSTEM && t.code === PATIENT_EVALUATION_STATUS.approved
      );
      const guidanceApproved = existingResponses.some(
        (r) => r.moduleUri === HSAT_ELIGIBILITY_MODULE_URI && r.status === 'success'
      );
      if (patientTagApproved || guidanceApproved) {
        showNotification({ color: 'orange', message: 'This patient has already been approved.' });
        handleClose();
        return;
      }

      await medplum.executeBot(botIdentifier('invite-patient'), { patientReference: createReference(patient) });

      const updatedTags = replaceTriageStatusTags(
        (patient.meta?.tag ?? []).filter((t) => t.system !== POLARIS_EVALUATION_TAG_SYSTEM),
        PATIENT_TRIAGE_STATUS.hsatApprovedCm
      );
      updatedTags.push({
        system: POLARIS_EVALUATION_TAG_SYSTEM,
        code: PATIENT_EVALUATION_STATUS.approved,
      });

      const updatedPatient: Patient = {
        ...patient,
        meta: { ...patient.meta, tag: updatedTags },
      };
      const guidanceResponse: GuidanceResponse = withTenantScope({
        ...buildBaseResource(),
        status: 'success',
      });

      await medplum.executeBatch({
        resourceType: 'Bundle',
        type: 'transaction',
        entry: [
          {
            resource: updatedPatient,
            request: { method: 'PUT', url: `Patient/${patientId}` },
          },
          {
            resource: guidanceResponse,
            request: { method: 'POST', url: 'GuidanceResponse' },
          },
        ],
      });
      await medplum.executeBot(botIdentifier('complete-workflow-task'), {
        patientId: resolveId(patient),
        actionId: 'approve-hsat-testing',
      });

      setApproved(true);
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err) });
    } finally {
      setSubmittingApproval(false);
    }
  }

  async function handleDenySubmit(): Promise<void> {
    if (!canEvaluate || !denialReason || !patientId) {
      return;
    }
    setSubmittingDenial(true);
    try {
      const patient = (await medplum.readResource('Patient', patientId)) as Patient;
      const updatedTags = replaceTriageStatusTags(
        (patient.meta?.tag ?? []).filter((t) => t.system !== POLARIS_EVALUATION_TAG_SYSTEM),
        PATIENT_TRIAGE_STATUS.denied
      );

      const updatedPatient: Patient = {
        ...patient,
        meta: { ...patient.meta, tag: updatedTags },
      };
      const reasonLabel = DENIAL_REASONS.find((r) => r.value === denialReason)?.label;
      const guidanceResponse: GuidanceResponse = withTenantScope<GuidanceResponse>({
        ...buildBaseResource(),
        status: 'failure',
        reasonCode: [
          {
            coding: [
              {
                system: DENIAL_REASON_SYSTEM,
                code: denialReason,
                display: reasonLabel,
              },
            ],
          },
        ],
      });

      await medplum.executeBatch({
        resourceType: 'Bundle',
        type: 'transaction',
        entry: [
          {
            resource: updatedPatient,
            request: { method: 'PUT', url: `Patient/${patientId}` },
          },
          {
            resource: guidanceResponse,
            request: { method: 'POST', url: 'GuidanceResponse' },
          },
        ],
      });
      handleClose();
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err) });
    } finally {
      setSubmittingDenial(false);
    }
  }

  return (
    <Modal withCloseButton={false} opened={opened} onClose={handleClose} centered size="lg" radius="md">
      <Stack gap="lg">
        <Stack gap={8} align="center">
          <Badge variant="light" color="grape" radius="xl" styles={{ root: { textTransform: 'none' } }}>
            Intake evaluation
          </Badge>
          <Title order={2} ta="center">
            Review HSAT eligibility
          </Title>
          <Text size="sm" c="dimmed" ta="center" maw={560}>
            Confirm whether this intake should continue toward HSAT ordering or be referred out for another next step.
          </Text>
        </Stack>

        <Paper withBorder radius="md" p="md">
          <Stack gap={6}>
            <Text size="xs" fw={700} tt="uppercase" c="dimmed">
              Patient
            </Text>
            <Anchor
              fw={700}
              td="underline"
              onClick={() => {
                handleClose();
                return navigate(`/Patient/${patientId}`);
              }}
              style={{ cursor: 'pointer', width: 'fit-content' }}
            >
              {patientName}
            </Anchor>
          </Stack>
        </Paper>

        <Box
          p="xl"
          style={{
            width: '100%',
            background: 'var(--mantine-color-gray-0)',
            borderRadius: 'var(--mantine-radius-md)',
          }}
        >
          <Stack gap="lg" align="center">
            {approved && (
              <>
                <Title order={3} ta="center">
                  Patient status has been updated.
                </Title>
                <Text size="sm" c="dimmed" ta="center" maw={480}>
                  Intake review complete. Patient can continue through next triage step.
                </Text>
                <Group justify="center" gap="sm">
                  <Button color="blue" onClick={handleClose}>
                    Finish
                  </Button>
                </Group>
              </>
            )}

            {denied && !approved && (
              <>
                <Stack gap="xs" align="center">
                  <Title order={3} ta="center">
                    Decision: Ineligible for HSAT
                  </Title>
                  <Text size="sm" c="dimmed" ta="center">
                    Marking this intake as ineligible records the reason and updates the patient queue.
                  </Text>
                  {isCareCoordinator && (
                    <Badge variant="light" color="orange" radius="xl" styles={{ root: { textTransform: 'none' } }}>
                      Care coordinator recommendation only
                    </Badge>
                  )}
                  {isCareCoordinator && (
                    <Text size="sm" c="dimmed" ta="center" maw={460}>
                      Because you are a Care Coordinator and not a Physician, this evaluation will be logged as a
                      recommendation. A Physician will review your recommendation and make a final decision.
                    </Text>
                  )}
                </Stack>
                <Select
                  label={<Text fw="bold">Reason for decision:</Text>}
                  placeholder="Select a reason"
                  value={denialReason}
                  maw="420px"
                  onChange={(v) => {
                    setDenialReason(v ?? '');
                  }}
                  data={DENIAL_REASONS}
                  w="100%"
                />
                <Group justify="center" gap="sm">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDenied(false);
                      setDenialReason('');
                    }}
                  >
                    Back
                  </Button>
                  <Button color="blue" disabled={!denialReason} loading={submittingDenial} onClick={handleDenySubmit}>
                    Submit
                  </Button>
                </Group>
              </>
            )}

            {!approved && !denied && (
              <>
                <Title order={3} ta="center">
                  Is the patient eligible for HSAT?
                </Title>
                {alreadyApproved ? (
                  <Text size="sm" c="orange" ta="center">
                    This patient has already been approved for HSAT and cannot be approved again.
                  </Text>
                ) : (
                  <Text size="sm" c="dimmed" ta="center">
                    Approving the patient for HSAT testing will enable them to order a test.
                  </Text>
                )}
                <Text size="sm" c="dimmed" ta="center" maw={460}>
                  Their System Status will remain &quot;Prospect&quot; until the HSAT is ordered, and their Triage
                  Status will be updated from &quot;Intake&quot; to &quot;Approved by [provider] for HSAT.&quot; The
                  patient will be notified whether you approve or deny their HSAT.
                </Text>
                {isCareCoordinator && (
                  <Badge variant="light" color="orange" radius="xl" styles={{ root: { textTransform: 'none' } }}>
                    Care coordinator approval records recommendation
                  </Badge>
                )}
                <Group justify="center" gap="sm">
                  <Button variant="outline" onClick={handleClose}>
                    Back
                  </Button>
                  <Button
                    variant="outline"
                    color="red"
                    onClick={() => {
                      setDenied(true);
                    }}
                  >
                    Deny HSAT
                  </Button>
                  <Button
                    color="blue"
                    disabled={alreadyApproved || loadingStatus}
                    loading={submittingApproval || loadingStatus}
                    onClick={handleApproveSubmit}
                  >
                    Approve for HSAT
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Box>
      </Stack>
    </Modal>
  );
}
